const letters = /^[a-zA-Z]+$/;
const numbers = /^[0-9]+$/;
const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];
export const monthAbbreviation = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];
export const monthName = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * parseIsoDate
 *
 * Parse an ISO 8601 date/time string into a normalized object of components and formatted strings.
 *
 * The function uses the built-in Date constructor to parse the provided ISO string and extracts
 * zero-padded string components for month, day, hour, minutes and seconds. It also produces
 * formatted date/time strings (YYYY-MM-DD, HH:MM:SS and HH:MM) and attempts to derive a
 * time zone abbreviation via Intl.DateTimeFormat (falls back to "UTC" when not available).
 *
 * Note: the parsing is delegated to the JS Date implementation; inputs should be valid ISO 8601
 * strings (for example "2025-11-25T14:30:00Z" or "2025-11-25T09:30:00-05:00"). Month handling
 * accounts for JS Date's 0-indexed months when producing the two-digit month string.
 *
 * @param {string} isoString - An ISO 8601 date/time string to parse.
 * @returns {{
 *   year: number,
 *   month: string,
 *   day: string,
 *   hour: string,
 *   minutes: string,
 *   seconds: string,
 *   yyyyMmDd: string,
 *   longTime: string,
 *   shortTime: string,
 *   timeZoneAbbreviation: string
 * }} An object containing:
 *   - year: Full year as a number (e.g. 2025).
 *   - month: Two-digit month string, "01" through "12".
 *   - day: Two-digit day of month string, "01" through "31".
 *   - hour: Two-digit hour string in 24-hour form, "00" through "23".
 *   - minutes: Two-digit minutes string, "00" through "59".
 *   - seconds: Two-digit seconds string, "00" through "59".
 *   - yyyyMmDd: Date string in "YYYY-MM-DD" format.
 *   - longTime: Time string in "HH:MM:SS" format.
 *   - shortTime: Time string in "HH:MM" format.
 *   - timeZoneAbbreviation: Time zone abbreviation or offset as derived from Intl.DateTimeFormat, or "UTC" if not available.
 * @throws {Error} If the provided isoString cannot be parsed into a valid Date.
 *
 * @example
 * // returns an object with padded components and formatted strings
 * parseIsoDate("2025-11-25T14:30:00Z");
 */
export function parseIsoDate(isoString) {
  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid ISO 8601 date string provided.");
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed, so add 1
  const day = date.getDate().toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  // const offset = isoString.match(/[+-]\d{2}:\d{2}$/)?.[0];
  // console.log(originalOffset); // "-06:00"

  const yearMonthDay = `${year}-${month}-${day}`;
  const hourMinutesSeconds = `${hour}:${minutes}:${seconds}`;
  const hourMinutes = `${hour}:${minutes}`;

  // const month = date.getMonth() + 1; // Month is 0-indexed
  // const day = date.getDate();
  // const hour = date.getHours();
  // const minutes = date.getMinutes();
  // const seconds = date.getSeconds();

  // Get time zone abbreviation using Intl.DateTimeFormat
  const timeZoneAbbreviation = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "shortOffset", // or 'short' for a more common abbreviation
  })
    .format(date)
    .split(" ")[1]; // Extract the abbreviation part

  return {
    year: year,
    month: month,
    day: day,
    hour: hour,
    minutes: minutes,
    seconds: seconds,
    // offset: offset,
    yyyyMmDd: yearMonthDay,
    longTime: hourMinutesSeconds,
    shortTime: hourMinutes,
    timeZoneAbbreviation: timeZoneAbbreviation || "UTC", // Default to UTC if abbreviation not found
  };
}

/**
 * Convert a UTC ISO string + IANA timezone into:
 *   {
 *     date: Date (local wall-clock, stored as UTC internally),
 *     dayKey: "YYYY-MM-DD",
 *     time: "HH:MM",
 *     minutesSinceMidnight: number,
 *     tzAbbr: "PST" | "GMT-6" | etc.
 *   }
 *
 * If ianaTimezone is missing, falls back to parseIsoDate (system timezone).
 */
export function getLocalEndpointInfo(isoUtc, ianaTimezone) {
  if (!isoUtc) return null;
  try {
    const utc = new Date(isoUtc);
    if (Number.isNaN(utc.getTime())) return null;

    if (!ianaTimezone) {
      // Fallback: use parseIsoDate (system zone)
      const p = parseIsoDate(isoUtc);
      const minutesSinceMidnight =
        Number(p.hour || 0) * 60 + Number(p.minutes || 0);
      return {
        date: utc,
        dayKey: p.yyyyMmDd,
        time: p.shortTime,
        minutesSinceMidnight,
        tzAbbr: p.timeZoneAbbreviation || "UTC",
      };
    }

    // Use Intl.DateTimeFormat to get local parts in the given IANA zone
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
    });

    const parts = fmt.formatToParts(utc);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const year = Number(get("year"));
    const month = Number(get("month"));
    const day = Number(get("day"));
    const hour = Number(get("hour"));
    const minute = Number(get("minute"));
    const second = Number(get("second"));
    const tzAbbr = get("timeZoneName") || "UTC";

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    const dayKey = `${year}-${String(month).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
    const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0"
    )}`;
    const minutesSinceMidnight = hour * 60 + minute;

    //  *   {
    //  *     date: Date (local wall-clock, stored as UTC internally),
    //  *     dayKey: "YYYY-MM-DD",
    //  *     time: "HH:MM",
    //  *     minutesSinceMidnight: number,
    //  *     tzAbbr: "PST" | "GMT-6" | etc.
    //  *   }
    return { date, dayKey, time, minutesSinceMidnight, tzAbbr };
  } catch {
    return null;
  }
}

/**
 * parseLocalDateTime
 * -------------------
 * Build a JavaScript Date from a "YYYY-MM-DD" and "HH:MM" string.
 *
 * This uses the *local environment timezone* (browser / Node process),
 * which is exactly what we want here because:
 *   - _day has already been decided (via parseIsoDate + local rules)
 *   - timeKeyForSort gives us a "minutes from midnight" anchor
 *   - we only need a consistent numeric timeline for comparing segments
 *
 * @param {string} yyyyMmDd - e.g. "2025-10-25"
 * @param {string} hhMm     - e.g. "21:00"
 * @returns {Date}
 */
export function parseLocalDateTime(yyyyMmDd, hhMm) {
  if (!yyyyMmDd || !hhMm) {
    // Fallback: "now", but this really should not happen in normal flow.
    return new Date();
  }

  const [yearStr, monthStr, dayStr] = yyyyMmDd.split("-");
  const [hourStr, minuteStr] = hhMm.split(":");

  const year = Number(yearStr);
  const month = Number(monthStr) - 1; // JS months are 0-based
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  return new Date(year, month, day, hour, minute, 0, 0);
}

/**
 * Format two dates as a short month/day range string using the "en-US" locale.
 *
 * The function accepts values that the Date constructor can parse (Date objects,
 * ISO strings, or numeric timestamps). It formats each date using short month
 * names and numeric days (e.g. "Jan 1") and returns them joined with " - ".
 *
 * @param {Date|string|number} start - The start date (Date object, ISO string, or timestamp).
 * @param {Date|string|number} end - The end date (Date object, ISO string, or timestamp).
 * @returns {string} A formatted date range like "Jan 1 - Jan 5". If inputs are invalid,
 *                   the resulting strings may be "Invalid Date - Invalid Date".
 *
 * @example
 * formatShortMonthDateRange('2023-01-01', '2023-01-05'); // "Jan 1 - Jan 5"
 */
export const formatShortMonthDateRange = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const options = { month: "short", day: "numeric" };
  const startStr = startDate.toLocaleDateString("en-US", options);
  const endStr = endDate.toLocaleDateString("en-US", options);

  return `${startStr} - ${endStr}`;
};

/**
 * Calculate the number of days from now until the given start date.
 *
 * Uses Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24)), where "today" is the current Date/time.
 * Partial future days are rounded up (e.g., a trip later today will often return 1). Partial past days are
 * rounded toward zero (e.g., -0.5 days => 0). The result can be negative for dates sufficiently in the past.
 *
 * @param {Date|string|number} startDate - A value accepted by the Date constructor (Date object, ISO string, timestamp).
 * @returns {number} Integer number of days until startDate. Returns NaN if startDate is invalid.
 *
 * @example
 * // If now is 2025-11-25T10:00:00 and startDate is 2025-11-26T09:00:00:
 * // diff ≈ 23 hours -> Math.ceil(23/24) => 1
 * tMinusCount('2025-11-26T09:00:00'); // 1
 *
 * @example
 * // If startDate is a past date more than 24 hours ago:
 * tMinusCount(new Date('2025-11-20')); // negative integer
 *
 * @note This calculation is based on the current local time (new Date()), so results depend on the system clock and timezone.
 */
export const tMinusCount = (startDate) => {
  const today = new Date();
  const tripDate = new Date(startDate);
  const diffTime = tripDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Calculates the number of days from now until the provided end date.
 *
 * The difference is computed as (endDate - now) in milliseconds, divided by the number of
 * milliseconds in a day, and then rounded up with Math.ceil so that any partial day counts
 * as a full day. A positive result indicates the endDate is in the future; zero or a
 * negative result indicates the endDate is now or in the past.
 *
 * @param {Date|string|number} endDate - The target end date. Accepts a Date object, an ISO/date string, or a timestamp.
 * @returns {number} The number of days until endDate (integer). Returns NaN if endDate is invalid.
 *
 * @example
 * // If now is 2025-11-25 10:00 and endDate is '2025-11-26T09:00:00',
 * // the function will return 1 (partial day counts as a full day).
 */
export const tPlusCount = (endDate) => {
  const today = new Date();
  const tripDate = new Date(endDate);
  const diffTime = tripDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * This function returns all four values you requested:
 *
 * isoString: Current moment in ISO 8601 format (e.g., "2025-11-25T14:30:45.123Z")
 * timestamp: Current moment as Unix timestamp in milliseconds
 * midnightIsoString: Today at midnight UTC in ISO 8601 format (e.g., "2025-11-25T00:00:00.000Z")
 * midnightTimestamp: Today at midnight UTC as Unix timestamp in milliseconds
 *
 * Usage example
 * const today = getMidnightInUtc();
 * console.log(today);
 *
 * {
 *  isoString: "2025-11-25T14:30:45.123Z",
 *  timestamp: 1732547445123,
 *  midnightIsoString: "2025-11-25T00:00:00.000Z",
 *  midnightTimestamp: 1732492800000
 * }
 */
export function getMidnightInUtc(dateStr = null) {
  const now = dateStr ? new Date(dateStr) : new Date();

  // Current date/time in ISO 8601 UTC string
  const isoString = now.toISOString();

  // Current date/time as UTC timestamp
  const timestamp = now.getTime();

  // Today's date at midnight UTC
  const midnightUtc = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  // Midnight ISO 8601 UTC string
  const midnightIsoString = midnightUtc.toISOString();

  // Midnight UTC timestamp
  const midnightTimestamp = midnightUtc.getTime();

  return {
    isoString,
    timestamp,
    midnightIsoString,
    midnightTimestamp,
  };
}

///////////////////////////////
// not sure if anything below here is useful or in use

// export function getLocalDateTimeInTimezone(isoUtcDateString, ianaTimezone) {
//   // Create a Date object from the ISO 8601 UTC string.
//   // The 'Z' in the string indicates UTC, so the Date object will represent UTC time.
//   const utcDate = new Date(isoUtcDateString);

//   // Define options for formatting, including the target timezone.
//   const options = {
//     year: "numeric",
//     month: "numeric",
//     day: "numeric",
//     hour: "numeric",
//     minute: "numeric",
//     second: "numeric",
//     timeZone: ianaTimezone, // Specify the IANA timezone
//     timeZoneName: "shortOffset", // Optional: Display the timezone offset (e.g., GMT-7)
//   };

//   // Create a DateTimeFormat object with the desired locale and options.
//   // 'en-US' is used as an example locale; choose one that fits your needs.
//   const formatter = new Intl.DateTimeFormat("en-US", options);
//   // const localDate = new Date(formatter.format(utcDate));

//   // Format the UTC date into the specified IANA timezone.
//   return formatter.format(utcDate); //localDate.toISOString();
// }

// export function convertUtcIsoToLocalIso(isoUtcDateString, ianaTimezone) {
//   const utcDate = new Date(isoUtcDateString);

//   const options = {
//     year: "numeric",
//     month: "2-digit",
//     day: "2-digit",
//     hour: "2-digit",
//     minute: "2-digit",
//     second: "2-digit",
//     hour12: false,
//     timeZone: ianaTimezone,
//   };

//   const formatter = new Intl.DateTimeFormat("en-CA", options);
//   const parts = formatter.formatToParts(utcDate);

//   // Extract parts
//   const partsObj = {};
//   parts.forEach(({ type, value }) => {
//     partsObj[type] = value;
//   });

//   // Get timezone offset
//   const localDate = new Date(
//     utcDate.toLocaleString("en-US", { timeZone: ianaTimezone })
//   );
//   const offset = (localDate - utcDate) / 60000; // offset in minutes
//   const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(
//     2,
//     "0"
//   );
//   const offsetMins = String(Math.abs(offset) % 60).padStart(2, "0");
//   const offsetSign = offset >= 0 ? "+" : "-";

//   // Construct ISO 8601 string
//   return `${partsObj.year}-${partsObj.month}-${partsObj.day}T${partsObj.hour}:${partsObj.minute}:${partsObj.second}${offsetSign}${offsetHours}:${offsetMins}`;
// }

// // Usage
// // const result = convertToTimezoneISO("2025-11-22T14:30:45Z", "America/Los_Angeles");
// // console.log(result); // "2025-11-22T06:30:45-08:00"

// // Example usage:
// // const isoUtcDate = '2025-11-22T21:25:00Z'; // November 22, 2025, 9:25 PM UTC
// // const targetTimezone = 'America/Denver';

// // const localDateTime = getLocalDateTimeInTimezone(isoUtcDate, targetTimezone);
// // console.log(localDateTime);

// /**
//  * Converts date components and timezone abbreviation to ISO 8601 format
//  * @param {number} year - Full year (e.g., 2024)
//  * @param {number} month - Month (1-12)
//  * @param {number} day - Day of month (1-31)
//  * @param {number} hours - Hours (0-23)
//  * @param {number} minutes - Minutes (0-59)
//  * @param {string} timezoneAbbr - Timezone abbreviation (PST, MST, CST, EST, etc.)
//  * @returns {string} Date string in YYYY-MM-DDTHH:mm:ss.sss±hh:mm format
//  */
// export function formatDateComponentsToIso8601(
//   year,
//   month,
//   day,
//   hours,
//   minutes,
//   timezoneAbbr
// ) {
//   // Normalize timezone abbreviation to uppercase
//   const tz = timezoneAbbr.toUpperCase();

//   // Get offset or throw error if unknown
//   const offsetHours = timezoneOffsets[tz];
//   if (offsetHours === undefined) {
//     throw new Error(`Unknown timezone abbreviation: ${timezoneAbbr}`);
//   }

//   // Pad numbers with leading zeros
//   const pad = (num, size = 2) => String(num).padStart(size, "0");

//   // Format date and time components
//   const dateStr = `${year}-${pad(month)}-${pad(day)}`;
//   const timeStr = `${pad(hours)}:${pad(minutes)}:00.000`;

//   // Format timezone offset
//   const offsetSign = offsetHours >= 0 ? "+" : "-";
//   const absOffset = Math.abs(offsetHours);
//   const offsetHoursInt = Math.floor(absOffset);
//   const offsetMinutes = Math.round((absOffset - offsetHoursInt) * 60);
//   const offsetStr = `${offsetSign}${pad(offsetHoursInt)}:${pad(offsetMinutes)}`;

//   return `${dateStr}T${timeStr}${offsetStr}`;
// }

// // Timezone offset mappings (in hours from UTC)
// // 2025-11-15T19:28:00Z (UTC)
// // 2025-11-15T12:28:00-07:00
// // UTC+14:00 (Line Islands, Kiribati): 2025-11-16T09:28:00+14:00
// // UTC+13:00 (Tonga, Samoa): 2025-11-16T08:28:00+13:00
// // UTC+12:00 (New Zealand Standard Time): 2025-11-16T07:28:00+12:00
// // UTC+11:00 (Solomon Islands, Vanuatu): 2025-11-16T06:28:00+11:00
// // UTC+10:00 (Australian Eastern Standard Time): 2025-11-16T05:28:00+10:00
// // UTC+09:00 (Japan Standard Time, Korea Standard Time): 2025-11-16T04:28:00+09:00
// // UTC+08:00 (China Standard Time, Australian Western Standard Time): 2025-11-16T03:28:00+08:00
// // UTC+07:00 (Thailand, Vietnam, Western Indonesia): 2025-11-16T02:28:00+07:00
// // UTC+06:00 (Bangladesh, Central Asia): 2025-11-16T01:28:00+06:00
// // UTC+05:30 (Indian Standard Time): 2025-11-16T00:58:00+05:30
// // UTC+05:00 (Pakistan, Uzbekistan): 2025-11-16T00:28:00+05:00
// // UTC+04:00 (United Arab Emirates, Azerbaijan): 2025-11-15T23:28:00+04:00
// // UTC+03:00 (Moscow Standard Time, East Africa Time): 2025-11-15T22:28:00+03:00
// // UTC+02:00 (Eastern European Time, Central Africa Time): 2025-11-15T21:28:00+02:00
// // UTC+01:00 (Central European Time, West Africa Time): 2025-11-15T20:28:00+01:00
// // UTC-01:00 (Azores Standard Time, Cape Verde Time): 2025-11-15T18:28:00-01:00
// // UTC-02:00 (South Georgia, South Sandwich Islands): 2025-11-15T17:28:00-02:00
// // UTC-03:00 (Brazil, Argentina, Greenland): 2025-11-15T16:28:00-03:00
// // UTC-04:00 (Atlantic Standard Time, Eastern Caribbean): 2025-11-15T15:28:00-04:00
// // UTC-05:00 (Eastern Standard Time, Colombia, Peru): 2025-11-15T14:28:00-05:00
// // UTC-06:00 (Central Standard Time, Central America): 2025-11-15T13:28:00-06:00
// // UTC-07:00 (Mountain Standard Time): 2025-11-15T12:28:00-07:00
// // UTC-08:00 (Pacific Standard Time): 2025-11-15T11:28:00-08:00
// // UTC-09:00 (Alaska Standard Time): 2025-11-15T10:28:00-09:00
// // UTC-10:00 (Hawaii-Aleutian Standard Time): 2025-11-15T09:28:00-10:00
// // UTC-11:00 (American Samoa, Niue): 2025-11-15T08:28:00-11:00
// // UTC-12:00 (Baker Island, Howland Island): 2025-11-15T07:28:00-12:00

// export const timezoneOffsets = {
//   // US Timezones (Standard Time)
//   PST: "-08:00", // Pacific Standard Time
//   MST: "-07:00", // Mountain Standard Time
//   CST: "-06:00", // Central Standard Time
//   EST: "-05:00", // Eastern Standard Time
//   AKST: "-09:00", // Alaska Standard Time
//   HST: "-10:00", // Hawaii Standard Time

//   // US Timezones (Daylight Time)
//   PDT: "-07:00", // Pacific Daylight Time
//   MDT: "-06:00", // Mountain Daylight Time
//   CDT: "-05:00", // Central Daylight Time
//   EDT: "-04:00", // Eastern Daylight Time
//   AKDT: "-08:00", // Alaska Daylight Time

//   // Other common timezones
//   UTC: "Z",
//   GMT: "Z",
//   BST: "+01:00", // British Summer Time
//   CET: "+01:00", // Central European Time
//   CEST: "+02:00", // Central European Summer Time
//   IST: "+05:30", // Indian Standard Time
//   JST: "+09:00", // Japan Standard Time
//   AEST: "+10:00", // Australian Eastern Standard Time
//   AEDT: "+11:00", // Australian Eastern Daylight Time
// };

// // Example usage:
// // formatDateWithTimezone(2024, 11, 15, 14, 30, 'PST')
// // Returns: "2024-11-15T14:30:00.000-08:00"

// // Map of timezone abbreviations to IANA timezone identifiers
// const timezoneMap = {
//   UTC: "UTC",
//   GMT: "GMT",
//   EST: "America/New_York",
//   EDT: "America/New_York",
//   CST: "America/Chicago",
//   CST: "America/Costa_Rica",
//   CDT: "America/Chicago",
//   MST: "America/Denver",
//   MDT: "America/Denver",
//   PST: "America/Los_Angeles",
//   PDT: "America/Los_Angeles",
//   AST: "America/Halifax",
//   ADT: "America/Halifax",
//   HST: "Pacific/Honolulu",
//   AKST: "America/Anchorage",
//   AKDT: "America/Anchorage",
//   CET: "Europe/Paris",
//   CEST: "Europe/Paris",
//   GMT: "Europe/London",
//   BST: "Europe/London",
//   IST: "Asia/Kolkata",
//   JST: "Asia/Tokyo",
//   AEST: "Australia/Sydney",
//   AEDT: "Australia/Sydney",
//   NZST: "Pacific/Auckland",
//   NZDT: "Pacific/Auckland",
// };

// /**
//  * Creates a Date.now() timestamp and converts it to a specific timezone
//  * @param {string} timezoneAbbr - Timezone abbreviation (e.g., 'EST', 'PST', 'UTC')
//  * @returns {object} Object containing the original timestamp and formatted date string
//  */
// export function getTimezoneTimestamp(timezoneAbbr = "UTC") {
//   // Create current timestamp
//   const timestamp = Date.now();

//   // Get the IANA timezone identifier from the abbreviation
//   const timezone = timezoneMap[timezoneAbbr.toUpperCase()] || "UTC";

//   // Create a date object from the timestamp
//   const date = new Date(timestamp);

//   // Format the date for the specific timezone
//   const formattedDate = date.toLocaleString("en-US", {
//     timeZone: timezone,
//     year: "numeric",
//     month: "2-digit",
//     day: "2-digit",
//     hour: "2-digit",
//     minute: "2-digit",
//     second: "2-digit",
//     hour12: false,
//   });

//   // Also get ISO string for the timezone
//   const isoString = date.toLocaleString("sv-SE", {
//     timeZone: timezone,
//   });

//   return {
//     timestamp: timestamp,
//     timezone: timezone,
//     timezoneAbbr: timezoneAbbr.toUpperCase(),
//     formattedDate: formattedDate,
//     isoFormat: isoString,
//     dateObject: date,
//   };
// }

// // Example usage:
// // console.log("UTC:", getTimezoneTimestamp());
// // console.log("EST:", getTimezoneTimestamp("EST"));
// // console.log("PST:", getTimezoneTimestamp("PST"));
// // console.log("JST:", getTimezoneTimestamp("JST"));

// export function getTimezoneAbbreviation(ianaTimezone, dateTime = new Date()) {
//   // Use the provided date or current date if not provided
//   const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

//   // Use Intl.DateTimeFormat to get the timezone abbreviation
//   const formatter = new Intl.DateTimeFormat("en-US", {
//     timeZone: ianaTimezone,
//     timeZoneName: "short",
//   });

//   // Extract the timezone abbreviation from the formatted string
//   const parts = formatter.formatToParts(date);
//   const timeZonePart = parts.find((part) => part.type === "timeZoneName");

//   return timeZonePart ? timeZonePart.value : null;
// }

// // Example usage:

// // Using current date/time
// // console.log(getTimezoneAbbreviation("America/Denver")); // MST or MDT (depending on current date)

// // // Using a specific date in winter (standard time)
// // const winterDate = new Date("2025-01-15");
// // console.log(getTimezoneAbbreviation("America/Denver", winterDate)); // MST

// // // Using a specific date in summer (daylight time)
// // const summerDate = new Date("2025-07-15");
// // console.log(getTimezoneAbbreviation("America/Denver", summerDate)); // MDT

// // // Other examples with specific dates
// // console.log(getTimezoneAbbreviation("America/New_York", winterDate)); // EST
// // console.log(getTimezoneAbbreviation("America/New_York", summerDate)); // EDT
// // console.log(getTimezoneAbbreviation("America/Los_Angeles", winterDate)); // PST
// // console.log(getTimezoneAbbreviation("America/Los_Angeles", summerDate)); // PDT
// // console.log(getTimezoneAbbreviation("Europe/London", winterDate)); // GMT
// // console.log(getTimezoneAbbreviation("Europe/London", summerDate)); // BST

// // Comprehensive timezone mapping
// const timezoneData = {
//   // US Timezones
//   "America/Los_Angeles": {
//     std: "PST",
//     dst: "PDT",
//     stdOffset: "-08:00",
//     dstOffset: "-07:00",
//   },
//   "America/Denver": {
//     std: "MST",
//     dst: "MDT",
//     stdOffset: "-07:00",
//     dstOffset: "-06:00",
//   },
//   "America/Phoenix": {
//     std: "MST",
//     dst: null,
//     stdOffset: "-07:00",
//     dstOffset: "-07:00",
//   }, // No DST
//   "America/Chicago": {
//     std: "CST",
//     dst: "CDT",
//     stdOffset: "-06:00",
//     dstOffset: "-05:00",
//   },
//   "America/New_York": {
//     std: "EST",
//     dst: "EDT",
//     stdOffset: "-05:00",
//     dstOffset: "-04:00",
//   },
//   "America/Anchorage": {
//     std: "AKST",
//     dst: "AKDT",
//     stdOffset: "-09:00",
//     dstOffset: "-08:00",
//   },
//   "Pacific/Honolulu": {
//     std: "HST",
//     dst: null,
//     stdOffset: "-10:00",
//     dstOffset: "-10:00",
//   }, // No DST

//   // Other common timezones
//   UTC: { std: "UTC", dst: null, stdOffset: "+00:00", dstOffset: "+00:00" },
//   "Europe/London": {
//     std: "GMT",
//     dst: "BST",
//     stdOffset: "+00:00",
//     dstOffset: "+01:00",
//   },
//   "Europe/Paris": {
//     std: "CET",
//     dst: "CEST",
//     stdOffset: "+01:00",
//     dstOffset: "+02:00",
//   },
//   "Asia/Kolkata": {
//     std: "IST",
//     dst: null,
//     stdOffset: "+05:30",
//     dstOffset: "+05:30",
//   },
//   "Asia/Tokyo": {
//     std: "JST",
//     dst: null,
//     stdOffset: "+09:00",
//     dstOffset: "+09:00",
//   },
//   "Australia/Sydney": {
//     std: "AEST",
//     dst: "AEDT",
//     stdOffset: "+10:00",
//     dstOffset: "+11:00",
//   },
// };

// /**
//  * Normalizes offset format to +/-HH:MM
//  */
// function normalizeOffset(offset) {
//   if (!offset) return null;

//   // Handle 'Z' for UTC
//   if (offset === "Z") return "+00:00";

//   // Ensure proper format
//   offset = offset.trim();

//   // Add + if no sign present
//   if (!offset.startsWith("+") && !offset.startsWith("-")) {
//     offset = "+" + offset;
//   }

//   // Ensure HH:MM format
//   if (offset.length === 3) {
//     // e.g., "+5"
//     offset = offset.slice(0, 1) + "0" + offset.slice(1) + ":00";
//   } else if (offset.length === 4) {
//     // e.g., "+05"
//     offset = offset + ":00";
//   }

//   return offset;
// }

// /**
//  * Converts timezone information between IANA timezone, abbreviation, offset, and DST flag
//  * @param {Object} input - Object with at least one of: ianaTimezone, timezoneAbbreviation, offset, dst
//  * @returns {Object} Complete timezone object with all properties
//  */
// function getTimezoneObject({
//   ianaTimezone = null,
//   timezoneAbbreviation = null,
//   offset = null,
//   dst = null,
// }) {
//   offset = normalizeOffset(offset);

//   // Case 1: IANA timezone provided
//   if (ianaTimezone) {
//     const tzInfo = timezoneData[ianaTimezone];
//     if (!tzInfo) {
//       throw new Error(`Unknown IANA timezone: ${ianaTimezone}`);
//     }

//     // Determine DST if not provided
//     const isDst = dst !== null ? dst : false;
//     const abbr = isDst && tzInfo.dst ? tzInfo.dst : tzInfo.std;
//     const tzOffset = isDst && tzInfo.dst ? tzInfo.dstOffset : tzInfo.stdOffset;

//     return {
//       ianaTimezone,
//       timezoneAbbreviation: abbr,
//       offset: tzOffset,
//       dst: isDst && tzInfo.dst !== null,
//     };
//   }

//   // Case 2: Abbreviation provided
//   if (timezoneAbbreviation) {
//     const abbr = timezoneAbbreviation.toUpperCase();

//     for (const [iana, info] of Object.entries(timezoneData)) {
//       if (info.std === abbr) {
//         return {
//           ianaTimezone: iana,
//           timezoneAbbreviation: abbr,
//           offset: info.stdOffset,
//           dst: false,
//         };
//       }
//       if (info.dst === abbr) {
//         return {
//           ianaTimezone: iana,
//           timezoneAbbreviation: abbr,
//           offset: info.dstOffset,
//           dst: true,
//         };
//       }
//     }

//     throw new Error(`Unknown timezone abbreviation: ${timezoneAbbreviation}`);
//   }

//   // Case 3: Offset provided
//   if (offset) {
//     const isDst = dst !== null ? dst : false;

//     for (const [iana, info] of Object.entries(timezoneData)) {
//       const targetOffset = isDst && info.dst ? info.dstOffset : info.stdOffset;

//       if (targetOffset === offset) {
//         // Check if DST status matches (if timezone supports DST)
//         if (isDst && !info.dst) continue; // Skip if DST requested but not available

//         const abbr = isDst && info.dst ? info.dst : info.std;
//         return {
//           ianaTimezone: iana,
//           timezoneAbbreviation: abbr,
//           offset: offset,
//           dst: isDst && info.dst !== null,
//         };
//       }
//     }

//     throw new Error(
//       `No timezone found for offset: ${offset} with DST: ${isDst}`
//     );
//   }

//   throw new Error(
//     "At least one of ianaTimezone, timezoneAbbreviation, or offset must be provided"
//   );
// }

// // // Example usage:
// // console.log('Example 1 - From offset:');
// // console.log(getTimezoneObject({ ianaTimezone: null, timezoneAbbreviation: null, offset: '-06:00', dst: true }));
// // // Returns: { ianaTimezone: 'America/Denver', timezoneAbbreviation: 'MDT', offset: '-06:00', dst: true }

// // console.log('\nExample 2 - From IANA:');
// // console.log(getTimezoneObject({ ianaTimezone: 'America/New_York', timezoneAbbreviation: null, offset: null, dst: false }));
// // // Returns: { ianaTimezone: 'America/New_York', timezoneAbbreviation: 'EST', offset: '-05:00', dst: false }

// // console.log('\nExample 3 - From abbreviation:');
// // console.log(getTimezoneObject({ ianaTimezone: null, timezoneAbbreviation: 'PDT', offset: null, dst: null }));
// // // Returns: { ianaTimezone: 'America/Los_Angeles', timezoneAbbreviation: 'PDT', offset: '-07:00', dst: true }

// /**
//  * Converts an ISO 8601 date-time string with a timezone offset to UTC in ISO 8601 format (ending in 'Z').
//  *
//  * @param {string} isoStringWithOffset - The input date-time string (e.g., '2024-02-06T06:30-06:00').
//  * @returns {string} The date-time string converted to UTC (e.g., '2024-02-06T12:30:00.000Z').
//  */
// export function convertIsoOffsetToUTC(isoStringWithOffset) {
//   // The JavaScript Date object automatically parses ISO 8601 strings,
//   // interpreting the provided offset correctly.
//   const dateObject = new Date(isoStringWithOffset);

//   // The toISOString() method formats the date into the ISO 8601 string format
//   // in UTC, appending the 'Z' (Zulu/UTC) indicator.
//   return dateObject.toISOString();
// }

// function isDaylightSavings(date) {
//   const jan1 = new Date(date.getFullYear(), 0, 1); // January 1st of the same year
//   const jul1 = new Date(date.getFullYear(), 6, 1); // July 1st of the same year

//   // Get the timezone offset for a date in January (likely non-DST)
//   const janOffset = jan1.getTimezoneOffset();
//   // Get the timezone offset for a date in July (likely DST in northern hemisphere)
//   const julOffset = jul1.getTimezoneOffset();

//   // Get the timezone offset for the specific date in question
//   const dateOffset = date.getTimezoneOffset();

//   // If the date's offset is different from the standard offset (e.g., January's),
//   // and it matches the offset during a known DST period (e.g., July's), it's likely DST.
//   // This approach assumes standard DST rules where the offset changes by a fixed amount.
//   return dateOffset !== janOffset && dateOffset === julOffset;
// }

// // const myDate = new Date("2025-07-15T14:30:00"); // Example date in July
// // const inDST = isDaylightSavings(myDate);
// // console.log(`Is the date in Daylight Savings Time? ${inDST}`);
